import React, { FC, useEffect, useState } from 'react';
import { Typography, TextField } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { AuthForm, AuthLink } from '../../components/Auth/AuthForm';
import { useSearchParams, useNavigate } from 'react-router-dom';

export const GuestConfirmEmailPage: FC = () => {
  const { confirmEmail, resendConfirmation, error } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isConfirming, setIsConfirming] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [hasAttemptedConfirmation, setHasAttemptedConfirmation] =
    useState(false);

  useEffect(() => {
    const token = searchParams.get('confirmation_token');
    if (token && !hasAttemptedConfirmation) {
      setHasAttemptedConfirmation(true);
      confirmEmail(token)
        .then(() => {
          setConfirmed(true);
          // Redirect to home page after a short delay
          setTimeout(() => navigate('/'), 2000);
        })
        .catch(() => {
          setIsConfirming(false);
        });
    } else if (!token) {
      setIsConfirming(false);
    }
  }, [searchParams, confirmEmail, hasAttemptedConfirmation, navigate]);

  const handleResendConfirmation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsResending(true);
    try {
      await resendConfirmation(email);
      // Show success message and redirect to sign in
      setTimeout(() => navigate('/guests/sign_in'), 2000);
    } finally {
      setIsResending(false);
    }
  };

  if (isConfirming) {
    return (
      <AuthForm title="Confirming Email" onSubmit={() => {}} submitLabel="">
        <Typography align="center">
          Please wait while we confirm your email address...
        </Typography>
      </AuthForm>
    );
  }

  if (confirmed) {
    return (
      <AuthForm title="Email Confirmed" onSubmit={() => {}} submitLabel="">
        <Typography align="center">
          Your email has been confirmed successfully. You are now signed in and
          will be redirected to the home page.
        </Typography>
        <Typography variant="body2">
          <AuthLink to="/guests/profile">Now let's get you set up!</AuthLink>
        </Typography>
      </AuthForm>
    );
  }

  return (
    <AuthForm
      title="Resend Confirmation"
      onSubmit={handleResendConfirmation}
      submitLabel={isResending ? 'Sending...' : 'Resend Confirmation'}
    >
      <Typography align="center" gutterBottom>
        {error ||
          'Please enter your email address to resend confirmation instructions.'}
      </Typography>
      <TextField
        autoFocus
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={isResending}
        required
      />
      <Typography variant="body2">
        <AuthLink to="/guests/sign_in">Back to Sign In</AuthLink>
      </Typography>
    </AuthForm>
  );
};
